import { CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Input, message, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as Component from 'components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VietnamUploadItem,
  VietnamUploadListParams,
  VietnamUploadStatus,
} from 'service/api/vietnamUploadService';
import { useIngestVietnamUpload, useVietnamUploads } from 'hooks/useVietnamUploads';
import { accountStore } from 'store/accountStore';

const { Title, Text } = Typography;

const STATUS_META: Record<VietnamUploadStatus, { color: string; label: string }> = {
  uploaded: { color: 'blue', label: 'Uploaded' },
  ingesting: { color: 'gold', label: 'Ingesting' },
  ingested: { color: 'green', label: 'Ingested' },
  failed: { color: 'red', label: 'Failed' },
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

const DEFAULT_PAGE_SIZE = 20;

/**
 * Vietnam 업로드 목록 + Ingest(→ Video 에셋 + 트랜스코딩).
 *
 * 화면은 하나다. 자기 company만 보이는지 전 CP가 보이는지는 **서버가 호출자 권한으로 결정**하고
 * (admin role이면 전 CP), 프론트는 스코프를 요청하지도 판정하지도 않는다.
 * 그래서 응답에 자기 것 아닌 company가 섞여 있으면 그때 Company 컬럼을 노출한다.
 *
 * 쓰기(Ingest)에는 서버 우회가 없다 — admin이라도 타 company 레코드는 404다.
 * 그래서 버튼은 `vietnam-uploads:ingest` 보유 + 자기 company 레코드일 때만 활성화한다.
 */
const VietnamUploadsList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<VietnamUploadStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string | undefined>(undefined);

  const params: VietnamUploadListParams = {
    page,
    page_size: pageSize,
    status: statusFilter,
    search: search || undefined,
  };

  const { data, isFetching, refetch } = useVietnamUploads(params);
  const ingest = useIngestVietnamUpload();

  const myCompanySlug = accountStore.getAccount()?.company?.slug;
  const canIngest = accountStore.canIngestVietnamUpload();
  // VN 전용 계정은 `/assets/edit/:id`에 도달할 수 없다 (assets 메뉴 키 없음 + API가 allowlist 밖).
  // 링크를 걸면 눌러도 튕기므로 텍스트로만 보여준다.
  const canOpenAsset = !accountStore.isLgChannelVietnam();

  const results = data?.results ?? [];
  const total = data?.count ?? 0;

  // 응답에 내 company 아닌 레코드가 있으면 = 서버가 전 CP를 내려준 계정이다.
  // 서버는 admin role 에만 전 CP를 반환하므로, 이 값이 참이면 곧 운영진이라는 뜻이다.
  // 프론트가 admin 여부를 직접 알 수 없어(role 이름은 permission 에 실리지 않는다)
  // 응답으로 판정한다.
  const companySlugs = Array.from(new Set(results.map(row => row.company_slug).filter(Boolean)));
  const isCrossCompanyView = companySlugs.some(slug => slug !== myCompanySlug);

  const visibleResults = companyFilter
    ? results.filter(row => row.company_slug === companyFilter)
    : results;

  const handleIngest = (record: VietnamUploadItem) => {
    ingest.mutate(record.id, {
      onSuccess: response => {
        const job = response.batch_jobs?.[0];
        if (job?.video_id) {
          message.success(`Asset #${job.video_id} created. Transcoding started.`);
        } else if (response.failures?.length) {
          message.error(`Ingest failed: ${response.failures[0] ?? 'Unknown error'}`);
        } else {
          message.success('Ingest started.');
        }
      },
      onError: (error: unknown) => {
        const response = (error as { response?: { status?: number; data?: any } })?.response;
        const status = response?.status;
        const detail = response?.data?.detail || response?.data?.error || (error as Error)?.message;
        if (status === 400) {
          message.error(detail || 'Already ingested or invalid key.');
        } else if (status === 403) {
          message.error('You do not have permission to ingest. (vietnam-uploads:ingest required)');
        } else if (status === 404) {
          message.error('You do not have access to this upload.');
        } else if (status === 500) {
          message.error('Ingest pipeline failed. Please retry.');
        } else {
          message.error(`Ingest failed: ${detail ?? 'Unknown error'}`);
        }
      },
    });
  };

  const renderIngestCell = (record: VietnamUploadItem) => {
    if (record.status === 'ingesting') {
      return <Text type="secondary">Ingesting…</Text>;
    }
    if (record.status === 'ingested') {
      return <Text type="secondary">Done</Text>;
    }
    // 타 company 레코드는 운영진만 실행할 수 있다. 서버는 admin 에게만 전 CP를 내려주므로
    // isCrossCompanyView 가 곧 운영진 판정이다 (프론트가 role 을 직접 알 수 없다).
    if (record.company_slug !== myCompanySlug && !isCrossCompanyView) {
      return (
        <Tooltip title="Only the owning CP can ingest this upload.">
          <Button size="small" icon={<CloudUploadOutlined />} disabled>
            Ingest
          </Button>
        </Tooltip>
      );
    }
    // uploaded | failed → Ingest 가능 (failed는 재시도)
    const isThisRowIngesting = ingest.isPending && ingest.variables === record.id;
    return (
      <Button
        type="primary"
        size="small"
        icon={<CloudUploadOutlined />}
        loading={isThisRowIngesting}
        disabled={ingest.isPending}
        onClick={() => handleIngest(record)}
      >
        {record.status === 'failed' ? 'Retry Ingest' : 'Ingest'}
      </Button>
    );
  };

  const columns: ColumnsType<VietnamUploadItem> = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      ellipsis: true,
      render: (value: string, record) => <span title={record.key}>{value}</span>,
    },
    // 전 CP가 내려온 계정에서만 노출한다 (자기 것만 보이는 계정에서는 항상 같은 값이라 무의미).
    ...(isCrossCompanyView
      ? ([
          {
            title: 'Company',
            dataIndex: 'company_slug',
            key: 'company_slug',
            width: 160,
            ellipsis: true,
            render: (value: string) => value || '-',
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
    // 같은 회사 안에도 계정이 여럿이라 누가 올렸는지 구분이 필요하다.
    {
      title: 'Uploaded By',
      dataIndex: 'uploaded_by',
      key: 'uploaded_by',
      width: 200,
      ellipsis: true,
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 110,
      render: (value: number | null) => formatFileSize(value),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created',
      key: 'created',
      width: 180,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VietnamUploadStatus) => {
        const meta = STATUS_META[status] ?? { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    // 생성된 에셋은 Ingest 권한과 무관하게 보여준다 — 조회만 가능한 CP도 자기 파일이
    // 어떤 에셋이 됐는지는 확인할 수 있어야 한다.
    //
    // 단 VN 전용 계정에게는 **링크를 걸지 않는다.** `/assets/edit/:id`는 `assets` 메뉴 키를
    // 요구하고 그 화면의 API는 BE allowlist 밖이라, 링크를 눌러도 튕기거나 403이 된다.
    // role을 더 줘도 열리지 않는다 — 막는 주체가 flag이기 때문이다. 그래서 id만 노출한다.
    {
      title: 'Asset',
      key: 'asset',
      width: 130,
      render: (_, record) => {
        if (!record.video_id) {
          return <Text type="secondary">-</Text>;
        }
        if (canOpenAsset) {
          return (
            <Button type="link" onClick={() => navigate(`/assets/edit/${record.video_id}`)}>
              Asset #{record.video_id}
            </Button>
          );
        }
        return <Text>Asset #{record.video_id}</Text>;
      },
    },
    // Ingest 실행 권한이 없는 계정에는 컬럼 자체를 만들지 않는다 (조회 전용 화면이 된다).
    ...(canIngest
      ? ([
          {
            title: 'Action',
            key: 'action',
            width: 180,
            render: (_, record) => renderIngestCell(record),
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
  ];

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Vietnam Video
          </Title>
          <Space>
            {accountStore.canAddVietnamUpload() && (
              <Button icon={<CloudUploadOutlined />} onClick={() => navigate('/assets/add-vn')}>
                Upload
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Space style={{ marginBottom: '16px' }} wrap>
          <Input.Search
            allowClear
            placeholder="Search file name"
            style={{ width: 260 }}
            onSearch={value => {
              setPage(1);
              setSearch(value);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={value => {
              setPage(1);
              setStatusFilter(value);
            }}
            options={(Object.keys(STATUS_META) as VietnamUploadStatus[]).map(s => ({
              value: s,
              label: STATUS_META[s].label,
            }))}
          />
          {/*
            서버에 company 필터 파라미터가 없어서 현재 페이지 안에서만 걸러진다.
            그래서 전체 건수가 아니라 "이 페이지" 기준임을 라벨에 드러낸다.
            전 CP 전체에서 특정 company만 보려면 서버에 ?company= 지원이 필요하다.
          */}
          {isCrossCompanyView && (
            <Select
              allowClear
              placeholder="Company (this page)"
              style={{ width: 200 }}
              value={companyFilter}
              onChange={value => setCompanyFilter(value)}
              options={companySlugs.map(slug => ({ value: slug, label: slug }))}
            />
          )}
        </Space>

        <Card bodyStyle={{ padding: 0 }}>
          <Table<VietnamUploadItem>
            rowKey="id"
            loading={isFetching}
            columns={columns}
            dataSource={visibleResults}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
                setCompanyFilter(undefined);
              },
            }}
          />
        </Card>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(VietnamUploadsList);
